import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useDispatchPromise } from '../../utils/AppUtils';
import { usePagination } from '../../hook/usePagination';
import { ContestManageContainer } from '../../components/ScreenContainer/ContestContainer/ContestManageContainer';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getContestManage, updateCampaignAwards, updateContest } from '../../redux/contest/actions';
import { managedCampaignSelector } from './selector';
import { TCampCat } from '../../redux/contest/types';
import { IContestRequest } from '../../redux/requests/types';
import { updateRequest } from '../../redux/requests/action';

const ManageContest = () => {
    const params = useParams();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const { contestId } = params;
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [sortBy, setSortBy] = useState<'createdAt' | 'votes' | 'jury_score'>('createdAt');
    const [activeTab, setActiveTab] = useState<string>('details');

    // Pagination for submissions data
    const {
        pagination: submissionPagination,
        setPagination: setSubmissionPagination,
        handleRowsChange: handleSubmissionRowsChange,
        handlePageChange: handleSubmissionPageChange,
    } = usePagination(1, 20);

    // Pagination for sales data
    const {
        pagination: salePagination,
        handleRowsChange: handleSaleRowsChange,
        handlePageChange: handleSalePageChange,
    } = usePagination(1, 20);

    const forceNoRefresh = useRef<boolean>(false);

    // Get data from redux store
    const {
        managedContest,
        managedContestLoading = false,
        managedContestError,
        updatingContest,
    } = useSelector(managedCampaignSelector);

    const getData = useCallback(
        async (category?: TCampCat) => {
            if (!contestId) return;

            const response: any = await dispatchPromise(
                getContestManage({
                    contestId: contestId,
                    searchTerm,
                    startDate,
                    endDate,
                    sortOrder,
                    sortBy,
                    submissionPage: submissionPagination.page,
                    submissionLimit: submissionPagination.rows,
                }),
            );

            // Update pagination state based on response
            if (response?.data) {
                if (response.data.submissions?.pagination) {
                    const { currentPage, pageSize, totalCount } = response.data.submissions.pagination;
                    setSubmissionPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount,
                    });
                }
            }

            forceNoRefresh.current = false;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            contestId,
            sortOrder,
            sortBy,
            endDate,
            submissionPagination.page,
            searchTerm,
            submissionPagination.rows,
            startDate,
        ],
    );

    const handleUpdateField = useCallback(
        (input: any) => {
            if (!contestId) return;

            dispatch(
                updateContest({
                    documentId: contestId,
                    payload: input,
                }),
            );
        },
        [contestId, dispatch],
    );

    // Handle clearing errors and retrying
    const handleClearError = useCallback(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        getData();
    }, [getData]);

    // Handle search
    const handleSearch = (search: string) => {
        setSearchTerm(search);
    };

    // Handle sort
    const handleSort = (sort: string) => {
        const [field, order] = sort.split('_');
        setSortBy(field as 'createdAt' | 'votes' | 'jury_score');
        setSortOrder(order as 'asc' | 'desc');
    };

    // Handle date filter changes
    const handleStartDateChange = (date: string) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date: string) => {
        setEndDate(date);
    };

    // Handle sort order change
    const handleSortOrderChange = (order: 'asc' | 'desc') => {
        setSortOrder(order);
    };

    // Clear filter handlers
    const handleClearSearch = () => {
        forceNoRefresh.current = true;
        setSearchTerm(undefined);
        getData();
    };

    const handleClearStartDate = () => {
        forceNoRefresh.current = true;
        setStartDate(undefined);
        getData();
    };

    const handleClearEndDate = () => {
        forceNoRefresh.current = true;
        setEndDate(undefined);
        getData();
    };

    const handleClearAllFilters = () => {
        forceNoRefresh.current = true;
        setSearchTerm(undefined);
        setStartDate(undefined);
        setEndDate(undefined);
        setSortBy('createdAt');
        setSortOrder('desc');
        getData();
    };

    // Handle tab change
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
    };

    // Handle edit contest details
    const handleEditDetails = (sectionName: string) => {};

    // Handle export to CSV
    const handleExportCSV = (type: 'submissions' | 'awards' | 'sales', data: any) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, type);
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        console.log('Why do we see this here');
        saveAs(dataBlob, `${type}_export.xlsx`);
    };

    const handleOnSaveChanges = (payload: Record<string, any>) => {
        if (contestId) {
            dispatch(
                updateContest({
                    documentId: contestId,
                    payload,
                }),
            );
        }
    };

    const handleUpdateRequest = (request: IContestRequest, withdraw?: boolean) => {
        dispatch(
            updateRequest({
                requestId: request._id,
                resubmit: true,
                withdraw,
            }),
        );
    };

    const handleDeleteAwardCategory = (index: number, awardId: string) => {
        if (contestId && managedContest?.contest && managedContest?.contest?.categories) {
            const specifiedCtategory = managedContest?.contest?.categories[index];
            if (specifiedCtategory && specifiedCtategory._id) {
                dispatch(
                    updateCampaignAwards({
                        action: 'remove',
                        awardCategoryId: awardId,
                        categoryId: specifiedCtategory._id,
                        contestId: contestId,
                    }),
                );
            }
        }
    };

    return (
        <MetaTags
            title={`Manage Contest | ${managedContest?.contest?.title || 'Loading...'}`}
            description={managedContest?.contest?.description || ''}>
            {managedContest && (
                <ContestManageContainer
                    contest={managedContest?.contest!}
                    submissions={managedContest?.submissions!}
                    awards={managedContest?.award_categories!}
                    sales={managedContest?.sales!}
                    votes={managedContest?.votes!}
                    discounts={managedContest?.discounts!}
                    loading={managedContestLoading}
                    updatingInProgress={updatingContest}
                    error={managedContestError}
                    activeTab={activeTab}
                    submissionPagination={submissionPagination}
                    salePagination={salePagination}
                    sortOrder={sortOrder}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onSortOrderChange={handleSortOrderChange}
                    onTabChange={handleTabChange}
                    onSubmissionRowsChange={handleSubmissionRowsChange}
                    onSubmissionPageChange={handleSubmissionPageChange}
                    onSaleRowsChange={handleSaleRowsChange}
                    onSalePageChange={handleSalePageChange}
                    onEditDetails={handleEditDetails}
                    onExportCSV={handleExportCSV}
                    onClearSearch={handleClearSearch}
                    onClearStartDate={handleClearStartDate}
                    onClearEndDate={handleClearEndDate}
                    onClearAllFilters={handleClearAllFilters}
                    onClearError={handleClearError}
                    onUpdate={handleUpdateField}
                    onImageChange={function (image: string, rawFile: File): void {
                        handleOnSaveChanges({ feature_image: [rawFile] });
                    }}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    onSortBy={sortBy => setSortBy(sortBy)}
                    onSaveChanges={handleOnSaveChanges}
                    onDeleteCategoryAward={handleDeleteAwardCategory}
                    onWithdraw={req => handleUpdateRequest(req, true)}
                    onResubmit={handleUpdateRequest}
                />
            )}
        </MetaTags>
    );
};

export { ManageContest };
